import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cn } from '@/utils/cn'

export type AccordionProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
export type AccordionItemProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
export type AccordionTriggerProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
export type AccordionContentProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>

/**
 * Collapsible sections built on Radix UI Accordion, so keyboard navigation and
 * the trigger/panel wiring assistive technology needs come from the primitive.
 *
 * `type="single"` keeps one section open at a time; `type="multiple"` allows
 * several. Pass `defaultValue` to choose which section starts expanded.
 */
export const Accordion = AccordionPrimitive.Root

export const AccordionItem = forwardRef<
  ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(function AccordionItem({ className, ...props }, ref) {
  return (
    <AccordionPrimitive.Item
      ref={ref}
      className={cn('border-b border-gray-200 last:border-b-0', className)}
      {...props}
    />
  )
})

export const AccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(function AccordionTrigger({ className, children, ...props }, ref) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between gap-2 py-3 text-left',
          'cursor-pointer font-medium transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          'focus-visible:ring-offset-1 [&[data-state=open]>svg]:rotate-180',
          className
        )}
        {...props}
      >
        {children}
        <svg
          className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 7.5 10 12.5 15 7.5" />
        </svg>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
})

export const AccordionContent = forwardRef<
  ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(function AccordionContent({ className, children, ...props }, ref) {
  return (
    <AccordionPrimitive.Content
      ref={ref}
      className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn('pb-3', className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
})
